import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { cartItems, eventLines } from '@theme/eme-product-set-core';

class EmeProductSet extends Component {
  requiredRefs = ['form', 'addButton', 'status', 'error'];
  #busy = false;

  async addAll(event) {
    event.preventDefault();
    if (this.#busy) return;
    const rows = (this.refs.rows ?? []).map((row) => ({
      variantId: row.dataset.variantId,
      available: row.dataset.available === 'true',
    }));
    const items = cartItems(rows);
    if (!items.length) return;

    this.#setBusy(true);
    this.#clearMessages();
    this.#announce(this.dataset.addingMessage ?? '');
    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({ action: 'add', context: 'product', lines: eventLines(items), promise: deferred.promise })
    );

    let data;
    try {
      const body = JSON.stringify({ items, sections: this.#cartSectionIds().join(',') });
      const response = await fetch(Theme.routes.cart_add_url, fetchConfig('json', { body }));
      data = await response.json();
      if (!response.ok || data.status) {
        const message = data.description || data.message || this.dataset.errorMessage || '';
        this.#showError(message);
        this.dispatchEvent(
          new CartErrorEvent({
            error: message,
            code: 'INVALID',
            detail: { description: data.description, errors: data.errors },
          })
        );
        await this.#settle(deferred, items, null, true);
        return;
      }
    } catch (error) {
      deferred.reject(error);
      this.#showError(this.dataset.errorMessage ?? '');
      this.dispatchEvent(
        new CartErrorEvent({ error: error?.message || this.dataset.errorMessage || '', code: 'SERVICE_UNAVAILABLE' })
      );
      this.#setBusy(false);
      return;
    }

    this.#announce(this.dataset.addedMessage ?? '');
    await this.#settle(deferred, items, data, false);
  }

  async #settle(deferred, items, data, failed) {
    try {
      const cart = await this.#fetchCart();
      if (cart) {
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: {
            items: cart.items,
            source: 'eme-product-set',
            sourceId: this.id,
            itemCount: items.length,
            sections: failed ? undefined : data?.sections,
            didError: failed,
          },
        });
      } else {
        deferred.reject(new Error('Cart unavailable'));
      }
    } catch (error) {
      deferred.reject(error);
    } finally {
      this.#setBusy(false);
    }
  }

  #cartSectionIds() {
    const ids = [];
    for (const element of document.querySelectorAll('cart-items-component')) {
      if (element instanceof HTMLElement && element.dataset.sectionId) ids.push(element.dataset.sectionId);
    }
    return ids;
  }

  async #fetchCart() {
    try {
      const response = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
      if (!response.ok) return null;
      return await response.json();
    } catch {
      return null;
    }
  }

  #setBusy(busy) {
    this.#busy = busy;
    this.refs.addButton.setAttribute('aria-busy', String(busy));
    this.refs.addButton.setAttribute('aria-disabled', String(busy));
  }

  #clearMessages() {
    this.refs.error.hidden = true;
    this.refs.error.textContent = '';
    this.refs.status.textContent = '';
  }

  #showError(message) {
    this.refs.status.textContent = '';
    this.refs.error.hidden = false;
    requestAnimationFrame(() => {
      this.refs.error.textContent = message;
    });
  }

  #announce(message) {
    this.refs.status.textContent = '';
    requestAnimationFrame(() => {
      this.refs.status.textContent = message;
    });
  }
}

if (!customElements.get('eme-product-set')) customElements.define('eme-product-set', EmeProductSet);
